/**
 * useMediaPermissions — the camera/mic permission machine that replaces the
 * static "camera blocked" banner with a real, recoverable flow.
 *
 * The pieces (the shared pattern, built here in the droom first — the customer
 * side is where it matters most since invites arrive over WhatsApp):
 *
 *  1. PRIMER before getUserMedia — an in-app explainer, THEN the native prompt is
 *     triggered from the user's Allow tap. An unexplained prompt reads as spyware
 *     and gets denied; priming raises first-grant rates.
 *  2. Permissions API state machine — navigator.permissions.query for camera/mic
 *     branches checking → primer (prompt) / granted / denied on entry, so a
 *     returning user who already granted skips the primer.
 *  3. Guided recovery on denied — NO site can re-trigger the native prompt once
 *     denied, so we render browser-detected instructions with the honest "you must
 *     change this in your browser" framing (never a fake retry that silently
 *     no-ops).
 *  4. Auto-resume — we subscribe to permissionStatus.onchange; when the user flips
 *     the toggle we re-request tracks and proceed WITHOUT a page reload. This is
 *     what makes recovery feel automatic.
 *  5. Honest edge cases — OS-level denial (the prompt never appears: getUserMedia
 *     rejects while the permission state stays 'prompt') → "check your system
 *     settings"; in-app webview (WhatsApp/Instagram/etc. via UA heuristics) →
 *     "open in Chrome/Safari" + copy-link. We DETECT and are honest; we don't
 *     pretend to fully solve what the platform won't let a web page solve.
 *
 * The probe getUserMedia is stopped immediately — it exists only to obtain the
 * grant; the real transport (LiveKit) acquires its own tracks afterwards with no
 * second prompt (permission is already granted).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { OsSupportNotice } from './osSupport';
import { osSupportNotice } from './osSupport';

export type BrowserFamily = 'chrome' | 'edge' | 'safari' | 'firefox' | 'samsung' | 'other';

export type PermissionUxPhase =
  | 'checking' // querying the Permissions API on entry
  | 'primer' // explainer shown; awaiting the user's Allow tap (prompt not yet fired)
  | 'requesting' // getUserMedia in flight (native prompt showing)
  | 'granted' // camera + mic granted — the media join may proceed
  | 'denied' // denied in-browser — guided recovery + auto-resume
  | 'os-denied' // the prompt never appeared — OS/system-level block
  | 'no-device' // no camera/mic present on the device
  | 'webview' // opened inside an in-app webview — must open in a real browser
  | 'unsupported'; // getUserMedia / mediaDevices unavailable

export interface MediaPermissionsState {
  readonly phase: PermissionUxPhase;
  /** True only when camera + mic are granted — the single gate the media join reads. */
  readonly canProceed: boolean;
  readonly browser: BrowserFamily;
  readonly isWebview: boolean;
  /** Calm, user-facing detail line (never a stack trace). */
  readonly detail: string | null;
  /** Browser-specific recovery steps for the denied / os-denied states. */
  readonly recoverySteps: readonly string[];
  /** Fire the primer's Allow — triggers the native prompt then records the grant. */
  readonly request: () => void;
  /** Re-attempt after the user says they changed the setting (denied/os-denied). */
  readonly retry: () => void;
  /** Copy the current link (the webview escape hatch — paste into Chrome/Safari). */
  readonly copyLink: () => void;
  readonly linkCopied: boolean;
  /**
   * Testmap #12 (owner mandate) — the honest below-support-floor advisory (deck key + the named
   * minimum), or null. ADVISORY only: an old device that works is never refused; the customer is
   * told plainly what to update if the camera fails.
   */
  readonly osNotice: OsSupportNotice | null;
}

// ── UA heuristics (best-effort; never load-bearing for security) ────────────────
// Exported for reuse by useCaptureCamera (S7-6) so the capture step and the call
// gate share ONE webview detector and ONE set of recovery copy.

export function detectBrowser(ua: string): BrowserFamily {
  if (/EdgA?\//.test(ua)) return 'edge';
  if (/SamsungBrowser/.test(ua)) return 'samsung';
  if (/Firefox|FxiOS/.test(ua)) return 'firefox';
  // Chrome check must precede Safari (Chrome UA also contains "Safari").
  if (/Chrome|CriOS|Chromium/.test(ua)) return 'chrome';
  if (/Safari/.test(ua)) return 'safari';
  return 'other';
}

/**
 * In-app webview detection. These containers routinely cannot grant camera/mic (or
 * silently block them), so we send the user to a real browser rather than into a
 * prompt that will never work. Matches the common social/chat webviews (this is the
 * one that matters most for the droom — invites arrive over WhatsApp).
 */
export function detectWebview(ua: string): boolean {
  if (/FBAN|FBAV|FB_IAB|Instagram|Line\/|Twitter|MicroMessenger|WhatsApp/i.test(ua)) return true;
  // Android System WebView marker.
  if (/; wv\)/.test(ua)) return true;
  // iOS in-app WKWebView: WebKit without the Safari or a known Chrome/Firefox token.
  const iOS = /iPhone|iPad|iPod/.test(ua);
  if (iOS && /AppleWebKit/.test(ua) && !/Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua)) {
    return true;
  }
  return false;
}

export function recoveryFor(browser: BrowserFamily, osLevel: boolean): string[] {
  if (osLevel) {
    return [
      'droom.perm.rec.os.1',
      'droom.perm.rec.os.2',
      'droom.perm.rec.os.3',
    ];
  }
  switch (browser) {
    case 'chrome':
    case 'edge':
    case 'samsung':
      return [
        'droom.perm.rec.chrome.1',
        'droom.perm.rec.chrome.2',
        'droom.perm.rec.chrome.3',
      ];
    case 'firefox':
      return [
        'droom.perm.rec.firefox.1',
        'droom.perm.rec.firefox.2',
        'droom.perm.rec.firefox.3',
      ];
    case 'safari':
      return [
        'droom.perm.rec.safari.1',
        'droom.perm.rec.safari.2',
        'droom.perm.rec.safari.3',
      ];
    default:
      return [
        'droom.perm.rec.generic.1',
        'droom.perm.rec.generic.2',
        'droom.perm.rec.generic.3',
      ];
  }
}

export async function queryPermissionState(
  name: 'camera' | 'microphone',
): Promise<PermissionState | null> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return null;
  try {
    const status = await navigator.permissions.query({ name: name as PermissionName });
    return status.state;
  } catch {
    // Firefox historically rejects camera/microphone queries — treat as unknown.
    return null;
  }
}

export function useMediaPermissions(enabled: boolean): MediaPermissionsState {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browser = detectBrowser(ua);
  const isWebview = detectWebview(ua);

  const [phase, setPhase] = useState<PermissionUxPhase>('checking');
  const [detail, setDetail] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const disposed = useRef(false);
  // Live PermissionStatus handles kept for the auto-resume onchange subscription.
  const watchers = useRef<PermissionStatus[]>([]);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      for (const w of watchers.current) w.onchange = null;
      watchers.current = [];
    };
  }, []);

  /** Probe getUserMedia to obtain the grant, then stop the probe tracks. */
  const probe = useCallback(async (): Promise<void> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPhase('unsupported');
      return;
    }
    setPhase('requesting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      // The probe exists only to secure the grant — release it so LiveKit owns the real tracks.
      for (const track of stream.getTracks()) track.stop();
      if (disposed.current) return;
      setDetail(null);
      setPhase('granted');
    } catch (e) {
      if (disposed.current) return;
      const err = e as DOMException;
      if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setDetail('droom.perm.nodevice.hookDetail');
        setPhase('no-device');
        return;
      }
      // NotAllowedError (or SecurityError): distinguish an in-browser denial from an
      // OS-level block — if the browser still reports 'prompt' it never recorded a
      // user denial, so the system swallowed the prompt (prompt never appeared).
      const after = await queryPermissionState('camera');
      setPhase(after === 'prompt' || after === null ? 'os-denied' : 'denied');
    }
  }, []);

  // Entry: detect webview, then branch on the Permissions API state.
  useEffect(() => {
    if (!enabled) {
      // Demo/disabled: the media transport is faked, so the gate is a no-op.
      setPhase('granted');
      return;
    }
    if (isWebview) {
      setPhase('webview');
      return;
    }
    let cancelled = false;
    (async () => {
      const [cam, mic] = await Promise.all([
        queryPermissionState('camera'),
        queryPermissionState('microphone'),
      ]);
      if (cancelled || disposed.current) return;
      if (cam === 'granted' && mic !== 'denied') {
        // Already granted (a returning customer) — skip the primer, secure real tracks.
        void probe();
      } else if (cam === 'denied' || mic === 'denied') {
        setPhase('denied');
      } else {
        // 'prompt' or unknown → show the primer; the prompt fires from the Allow tap.
        setPhase('primer');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, isWebview, probe]);

  // Auto-resume: while blocked, watch camera/mic; when both go grantable, re-probe.
  useEffect(() => {
    if (phase !== 'denied' && phase !== 'os-denied') return undefined;
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return undefined;
    let cancelled = false;

    const attach = async (name: 'camera' | 'microphone') => {
      try {
        const status = await navigator.permissions.query({ name: name as PermissionName });
        if (cancelled) return;
        status.onchange = () => {
          if (status.state === 'granted' && !disposed.current) {
            void probe(); // the toggle flipped — rejoin WITHOUT a reload
          }
        };
        watchers.current.push(status);
      } catch {
        /* Permissions API unavailable for this name — auto-resume degrades to the manual retry. */
      }
    };
    void attach('camera');
    void attach('microphone');

    return () => {
      cancelled = true;
      for (const w of watchers.current) w.onchange = null;
      watchers.current = [];
    };
  }, [phase, probe]);

  const copyLink = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(window.location.href).then(() => {
      if (disposed.current) return;
      setLinkCopied(true);
      window.setTimeout(() => {
        if (!disposed.current) setLinkCopied(false);
      }, 2500);
    });
  }, []);

  return {
    phase,
    canProceed: phase === 'granted',
    browser,
    isWebview,
    detail,
    recoverySteps: phase === 'os-denied' ? recoveryFor(browser, true) : recoveryFor(browser, false),
    request: () => void probe(),
    retry: () => void probe(),
    copyLink,
    linkCopied,
    osNotice: osSupportNotice(navigator.userAgent),
  };
}
